<template>
  <span>
    <!-- full size card START -->
    <v-card
      v-if="!minimized"
      color="white"
      height="100%"
      class="pa-2 text-left"
    >
      <!-- card top left status text START -->
      <div v-if="integration.config_id">
        <v-icon color="primary">mdi-link</v-icon>
        <span class="md-title primary--text">Connected</span>
      </div>
      <div v-else>
        <br>
      </div>
      <!-- card top left status text END -->

      <!-- integration logo, name and description START -->
      <v-img
        class="mt-2"
        :src="integration.logo"
        height="85px"
        contain
      />
      <v-card-text
        primary-title
        class="pa-0"
      >
        <div class="mt-3 mb-1 text-center">
          <h4 class="headline">{{ integration.name }}</h4>
          <div
            style="overflow:hidden;height:40px;text-overflow: ellipsis;"
          >{{ integration.description }}</div>
        </div>
      </v-card-text>
      <!-- integration logo, name and description END -->

      <!-- configuration edition START -->
      <v-card-actions>
        <v-btn
          block
          large
          @click="configureApp"
        >Configure</v-btn>
      </v-card-actions>
      <!-- configuration edition END -->
    </v-card>
    <!-- full size card END -->

    <!-- minimized card START -->
    <v-card
      v-else
      hover
      height="58px"
      @click.native="configureApp"
    >
      <v-img
        :src="integration.logo"
        height="35px"
        style="padding-top: 2px"
        contain
      />
      <v-card-text style="margin-top: -15px;">{{ integration.name }}</v-card-text>
    </v-card>
    <!-- minimized card END -->

    <!-- dialog START -->
    <v-dialog
      v-model="dialog"
      transition="dialog-bottom-transition"
      :overlay="false"
      width="50%"
    >
      <v-card>
        <!-- top bar START -->
        <v-toolbar
          dark
          color="secondary"
        >
          <v-btn
            icon
            dark
            @click.native="dialog = false; streams = {};singerDataTablesReady = false;"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Settings for {{ integration.name }}</v-toolbar-title>
        </v-toolbar>
        <!-- top bar END -->

        <!-- general content START -->
        <v-list-item
          three-line
          subheader
        >
          <v-list-item-content>
            <v-list-item-title>General</v-list-item-title>
            <v-list-item-subtitle>{{ integration.description }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <!-- general content END -->
        <!-- configuration selection START -->
        <v-select
          v-if="!addOnlyOne"
          v-model="selectedConfig"
          :items="integration.configs"
          item-text="name"
          item-value="id"
          label="Configuration"
          no-data-text="No configuration available"
          class="select-config"
          @change="id => updateSelectedConfig(id)"
        />
        <!-- configuration selection END -->

        <!-- configuration text-field START -->
        <v-text-field
          v-if="selectedConfig"
          ref="configName"
          v-model="selectedConfig['updated_name']"
          class="config-name-field"
          :label="isNewConfig ? 'New configuration name *' : 'Update configuration name *'"
        />
        <!-- configuration text-field END -->

        <!-- global checkbox START -->
        <v-row no-gutters>
          <v-checkbox
            v-model="isGlobal"
            label="Global configuration"
            :disabled="!project.id"
            class="global-checkbox"
          />
          <v-tooltip
            top
            class="no-select"
          >
            <template v-slot:activator="{ on }">
              <v-icon
                small
                class="global-tooltip"
                v-on="on"
              >mdi-help-circle</v-icon>
            </template>
            <span>Global objects are shared to your whole company</span>
          </v-tooltip>
        </v-row>
        <!-- global checkbox END -->

        <v-divider />

        <!-- singer-io streams selection START -->
        <template v-if="integration.is_singerio && tables.length">
          <v-row column>
            <v-col>
              <v-list
                id="select-table-columns-list"
                :style="'max-height: '+innerHeight/1.5+'px; overflow: auto;'"
              >
                <v-subheader>Let Himydata know what data you want in your warehouse.</v-subheader>
                <v-col
                  v-for="(table, index) in tables"

                  :ref="'table_columns_'+table.name"
                  :key="index"
                  class="xs"
                  style="padding:5px 15px;"
                >
                  <v-card>
                    <v-container>
                      <div role="table-sync">
                        <div
                          style="display: flow-root;cursor:pointer;"
                          @click="callSingerioDiscoverFields(table,index)"
                        >
                          <div role="title-label">Table</div>
                          <span role="title">{{ table.name }}</span>
                          <div
                            style="float:right;"
                            :class="(table.sync ? 'active' : '') + ' sync'"
                          >
                            <v-btn
                              text
                              small
                            >
                              <v-icon role="icon">{{ table.sync ? 'mdi-sync' : 'mdi-sync-off' }}</v-icon>
                              {{ table.sync ? 'Synchronized' : 'Not Synchronized' }}
                            </v-btn>
                          </div>
                        </div>
                        <v-col class="columns">
                          <v-expand-transition>
                            <v-col
                              v-show="table.sync"
                              cols="12"
                              class="dropdown text-center"
                            >
                              <span
                                style="cursor:pointer;"
                                @click="table.showColumns=!table.showColumns"
                              >
                                <v-icon>{{ table.showColumns ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                Manage selected columns
                              </span>
                            </v-col>
                          </v-expand-transition>
                          <v-expand-transition>
                            <div
                              v-if="table.showColumns && table.sync"
                              role="columns-table"
                            >
                              <v-data-table
                                class="scrollBody"
                                hide-default-footer
                                :headers="[ { text: 'Name', value: 'name' }, { text: 'Type', value: 'type' },{ text: 'Key', value: 'primary_key' } ]"
                                :items="table.columns"
                                item-key="name"
                                select-all
                              >
                                <template v-slot:headers="props">
                                  <tr>
                                    <th>
                                      <v-checkbox
                                        v-model="table.allColumnsSelected"
                                        primary
                                        hide-details
                                        @change="e => handleSelectAll(e, table)"
                                      />
                                    </th>
                                    <th
                                      v-for="header in props.headers"
                                      :key="header.text"
                                    >{{ header.text }}</th>
                                  </tr>
                                </template>
                                <template v-slot:items="props">
                                  <td>
                                    <v-checkbox
                                      v-model="props.item.selected"
                                      primary
                                      hide-details
                                      :disabled="props.item.primary_key"
                                      @change="e => handleSelectColumn(e,table)"
                                    />
                                  </td>
                                  <td>{{ props.item.name }}</td>
                                  <td>{{ props.item.type }}</td>
                                  <td>
                                    <span v-if="props.item.primary_key">Primary key</span>
                                  </td>
                                </template>
                              </v-data-table>
                            </div>
                          </v-expand-transition>
                        </v-col>
                      </div>
                    </v-container>
                  </v-card>
                </v-col>
              </v-list>
            </v-col>
            <v-col
              class="text-center xs"
              style="padding: 10px 0px 20px;"
            >
              <v-btn
                color="secondary"
                :disabled="singerCommandRunning"
                @click="handleClickPreviousSingerStreams"
              >
                <v-icon>mdi-chevron-left</v-icon>Previous
              </v-btn>
              <v-btn
                color="primary"
                :disabled="tables.filter(o => { return o.sync }).length <= 0 || singerCommandRunning"
                :loading="singerCommandRunning"
                @click="handleClickCall"
              >
                Call &nbsp;
                <v-icon small>mdi-send</v-icon>
              </v-btn>
            </v-col>
            <v-divider vertical />
          </v-row>
        </template>
        <!-- singer-io streams selection END -->

        <!-- integration configuration form START -->
        <template v-else>
          <!-- user settings content START -->
          <v-subheader>User settings</v-subheader>
          <v-card-text>
            <v-form>
              <template
                v-for="(el, index) in integration.config"
                v-key="index"
              >
                <v-col :key="index">
                  {{ integration.name + '.' + el.name }}
                  <span
                    v-if="el.required"
                    style="color: red"
                  >*</span>
                  <v-text-field
                    v-if="selectedConfig.values"
                    v-model="selectedConfig.values[el.name]"
                    :disabled="el.readonly"
                    :placeholder="el.placeholder"
                    :type="el.to_hide ? el.type ? el.type : 'password' : 'text'"
                    :append-icon="el.to_hide ? el.type && el.type === 'text' ? 'mdi-eye-off' : 'mdi-eye' : ''"
                    @click:append="toggle(el)"
                  />
                  <!-- TODO: validation -->
                  <!-- :v-validate="el.required ? 'required' : ''"
                    :data-vv-name="integration.name + '.' + el.name"
                  :error-messages="errors.collect(integration.name + '.' + el.name)"-->
                </v-col>
              </template>
            </v-form>
            <v-row>
              <v-col
                v-if="missingMandatoryField"
                class="text-center"
                cols="12"
              >
                <div class="warning--text text-center">
                  <v-icon
                    x-large
                    color="warning"
                  >mdi-alert</v-icon>
                  <v-spacer />
                  <span style="white-space: pre-line">All mandatory fields must be filled</span>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col />
              <v-col>
                <v-btn
                  block
                  color="info"
                  :disabled="integration.config_id === null || loading"
                  text
                  @click.native="deleteConf()"
                >{{ isNewConfig ? "Reset" : "Delete" }}</v-btn>
              </v-col>
              <v-col>
                <v-btn
                  color="primary"
                  block
                  :disabled="missingMandatoryField"
                  :loading="loading"
                  @click.native="save()"
                >{{ isNewConfig ? "Create" : "Save" }}</v-btn>
              </v-col>
              <v-col />
            </v-row>
          </v-card-text>
          <!-- user settings content END -->
        </template>
        <!-- integration configuration form START -->
      </v-card>
    </v-dialog>
    <!-- dialog END -->
  </span>
</template>
<script>
  import IntegrationService from '../../Dashboard/Views/Integrations/services/IntegrationService'
  import SingerService from '../../Dashboard/Views/Datastore/services/SingerService'
  export default {
    $_veeValidate: {
      validator: 'new',
    },
    props: {
      integration: {
        type: Object,
        default: function () {
          return {
            name: '',
            description: '',
            slug: '',
            updated_at: '',
            is_singerio: false,
            type: '',
            configs: '',
          }
        },
      },
      project: {
        type: Object,
        default: function () {
          return {
            name: '',
          }
        },
      },
      // addOnlyOne is used when you want to only be able to add one configuration to your integration
      // Used for creating a new integration from the project page for example
      addOnlyOne: {
        type: Boolean,
        default: false,
      },
      index: Number,
      minimized: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        tables: [],
        dialog: false,
        loading: false,
        streams: [],
        discoveredStreams: [],
        singerDataTablesReady: false,
        singerStep: 0,
        singerCommandRunning: false,
        selectedConfig: {},
        isNewConfig: false,
        isGlobal: true,
      }
    },
    computed: {
      innerHeight: function () {
        return window.innerHeight
      },
      missingMandatoryField: function () {
        if (
          !this.selectedConfig ||
          !this.selectedConfig.updated_name ||
          this.selectedConfig.updated_name.length == 0
        ) {
          return true
        }
        let missing = false
        this.integration.config.some(field => {
          if (
            field.required &&
            (!this.selectedConfig.values[field.name] ||
            this.selectedConfig.values[field.name].length == 0)
          ) {
            missing = true
            return true
          }
        })
        return missing
      },
    },
    mounted () {
      this.$validator.localize('fr', this.signinDictionary)
      this.updateSelectedConfig()
    },
    methods: {
      configureApp () {
        this.dialog = true
      },
      callSingerioDiscoverFields (table, index) {
        const t = this
        IntegrationService.singerioDiscoverFields(t.integration.slug, table.name)
          .then(function (response) {
            const cols = []
            table.columns = _.map(response.data, (o, i) => {
              o.selected = true
              return o
            })

            table.sync = !table.sync
            if (table.sync) {
              for (let i = 0; i < t.tables.length; i++) { t.tables[i].showColumns = index == i }
            }
            setTimeout(() => {
              const el = 'table_columns_' + table.name
              if (t.$refs[el]) {
                t.$vuetify.goTo(t.$refs[el][0], {
                  duration: 500,
                  offset: 220,
                  easing: 'easeInOutCubic',
                  container: '#select-table-columns-list',
                })
              }
            }, 500)
          })
          .catch(function (error) {
            t.$store.commit('push', {
              msg: 'Error retrieving ' + table.name + ' fields',
              color: 'error',
            })
          })
      },
      save () {
        this.$validator.validateAll().then(result => {
          var t = this
          if (result) {
            IntegrationService.updateConfiguration(this.selectedConfig, this)
              .then(function (response) {
                t.isNewConfig = false
                // Add new config inside the dropdown
                if (t.addOnlyOne) {
                  t.$store.commit('push', {
                    msg: 'Configuration added',
                    color: 'success',
                  })
                  t.dialog = false
                  t.updateSelectedConfig()
                  return
                }

                if (t.selectedConfig.id == -1) {
                  t.integration.configs.push(response.data)
                  t.$store.commit('push', {
                    msg: 'Configuration added',
                    color: 'success',
                  })
                } else {
                  t.$store.commit('push', {
                    msg: 'Configuration updated',
                    color: 'success',
                  })
                }

                t.selectedConfig.id = response.data.id
                t.selectedConfig.name = response.data.name
                t.selectedConfig.values = response.data.values
                // If Singerio
                if (t.integration.is_singerio) {
                  t.callSingerioDiscover()
                }
              })
              .catch(function (error) {
              /* t.$store.commit('push', {msg: error.response.data.values[0], color: 'error'}); */
              })
          }
        })
      },
      callSingerioDiscover () {
        var t = this
        this.loading = true
        IntegrationService.singerioDiscover(this.integration.slug)
          .then(function (response) {
            if (response.data.length && t.dialog) {
              t.streams = response.data
              t.tables = _.map(response.data, (o, i) => {
                return {
                  name: o,
                  sync: false,
                  showColumns: false,
                  columns: [],
                  allColumnsSelected: true,
                }
              })
            }
            t.loading = false
          })
          .catch(function (error) {
            let errorMessage = ''

            for (let i = 0; i < error.response.data.error.length; i++) {
              if (i > 0) {
                errorMessage += '\n'
              }
              errorMessage += error.response.data.error[i]
            }

            t.$store.commit('push', { msg: errorMessage, color: 'error' })
            t.loading = false
          })
      },
      deleteConf () {
        // For new configs, just reset the form
        if (this.isNewConfig) {
          this.updateSelectedConfig(-1)
          return
        }
        const t = this
        IntegrationService.deleteConf(this.selectedConfig.id, this)
          .then(function (response) {
            // Remove deleted configuration
            const index = _.findIndex(t.integration.configs, function (el) {
              return el.name === t.selectedConfig.name
            })
            t.integration.configs.splice(index, 1)
            t.updateSelectedConfig()
            // TODO: error message from back?
            t.$store.commit('push', {
              msg: 'Configuration deleted',
              color: 'success',
            })
          })
          .catch(function (error) {
            t.$store.commit('push', { msg: 'error', color: 'error' })
          })
      },
      handleClickPreviousSingerStreams: function () {
        this.tables = []
      },
      handleSelectColumn: function (e, table) {
        table.allColumnsSelected =
          table.columns.filter(o => {
            return !o.selected
          }) <= 0
      },
      handleSelectAll: function (e, table) {
        if (e) {
          _.map(table.columns, (o, i) => {
            o.selected = true
          })
        } else {
          _.map(table.columns, (o, i) => {
            o.selected = !!o.primary_key
          })
        }
      },
      handleClickCall: async function () {
        this.singerCommandRunning = true
        const t = this
        const promises = []
        const tables = _.filter(t.tables, o => {
          return (
            o.sync &&
            _.filter(o.columns, k => {
              return k.selected
            }).length > 0
          )
        })
        if (tables.length <= 0) {
          t.$store.commit('push', {
            msg: 'You have not synchronized any table yet',
            color: 'error',
          })
          return
        }
        _.map(tables, table => {
          const fields = _.map(
            _.filter(table.columns, k => {
              return k.selected
            }),
            'name'
          )
          promises.push(
            new Promise(async function (resolve) {
              await SingerService.configFields(
                t.integration.slug,
                table.name,
                fields
              )
              resolve('Config done')
            })
          )
        })

        Promise.all(promises)
          .then(function () {
            t.$store.commit('push', {
              msg: t.integration.name + ' sync updated',
              color: 'success',
            })
            t.singerCommandRunning = false
            t.dialog = false
            t.singerDataTablesReady = false
          })
          .finally(() => {
            t.tables = []
            t.singerDataTablesReady = false
          })
      },
      toggle (el) {
        el.type = el.type === 'text' ? 'password' : 'text'
        this.$forceUpdate()
      },
      updateSelectedConfig (id = 0) {
        // id == -1 when clicking on the create button
        if (this.integration.configs.length == 0 || id == -1 || this.addOnlyOne) {
          this.isNewConfig = true
          if (id == -1) {
            this.$nextTick(() => this.$refs.configName.focus())
          }
          this.selectedConfig = { id: -1, name: '' }
          this.$set(this.selectedConfig, 'updated_name', '')
          this.$set(this.selectedConfig, 'values', {})
          for (const key in this.integration.config) {
            if (!this.integration.config[key].readonly) {
              this.$set(
                this.selectedConfig.values,
                this.integration.config[key].name,
                ''
              )
            } else {
              this.$set(
                this.selectedConfig.values,
                this.integration.config[key].name,
                this.integration.config[key].value
              )
            }
          }
        } else {
          this.isNewConfig = false
          // id == 0 at initialization
          if (id == 0) {
            this.selectedConfig = this.integration.configs[0]
          } else {
            let found = false
            this.integration.configs.some(config => {
              if (config.id == id) {
                found = true
                this.selectedConfig = config
                return true
              }
            })
            if (!found) {
              this.updateSelectedConfig(-1)
            }
          }
          this.$set(
            this.selectedConfig,
            'updated_name',
            this.selectedConfig.name
          )
        }
        for (const key in this.integration.config) {
          if (this.integration.config[key].readonly) {
            this.$set(
              this.selectedConfig.values,
              this.integration.config[key].name,
              this.integration.config[key].value
            )
          }
        }
      },
    },
  }
</script>

<style>
.integration-description {
  overflow: hidden;
  height: 40px;
  text-overflow: ellipsis;
}
.create-config-btn {
  position: absolute;
  top: 70px;
  right: 20px;
}
[role="table-sync"] .columns .dropdown > span > i {
  margin: -4px 2px;
}
[role="table-sync"] .columns .dropdown {
  text-align: center;
}
[role="table-sync"] .columns .dropdown > span {
  display: inline-block;
  margin: 7px 0px 0px;
  font-size: 13px;
  color: #c7c7c7;
}
[role="table-sync"] .columns [role="columns-table"] {
  margin-top: 15px;
  padding-top: 20px;
}
[role="table-sync"] [role="title"] {
  font-size: 20px;
}
[role="table-sync"] .sync {
  margin-top: -8px;
}
[role="table-sync"] .sync .v-btn__content {
  color: lightgray;
}
[role="table-sync"] .active.sync .v-btn__content {
  color: #1fcc95;
}
[role="table-sync"] .sync [role="icon"] {
  font-size: 17px;
  margin: 0 5px 0px 0px;
}
.scrollBody table tbody {
  display: block;
  max-height: 280px;
  overflow: auto;
}
.scrollBody table thead {
  text-align: left;
}
.scrollBody table thead,
.scrollBody table tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}
.scrollBody table thead th:first-child {
  width: 50px;
}
.scrollBody table tbody tr td:first-child {
  width: 50px;
}
.config-name-field {
  padding: 10px 15px 0;
}
.global-checkbox {
  padding: 0 0 0 15px;
}
.global-tooltip {
  margin-top: -8px;
  margin-left: 5px;
}
.select-config {
  padding: 0 15px;
}
</style>
